import React from 'react';
import {useTranslation} from 'react-i18next';

import {isSameDay} from 'date-fns/isSameDay';
import {isValid as isValidDate} from 'date-fns/isValid';
import {parse as parseDate} from 'date-fns/parse';

import {ECalendarType, TCalendarType, isAvailableDate} from '../Calendar';
import * as S from './style';
import {WIDE_LETTERS_COMPENSATOR} from './types';
import {formatInputDate, formatInputValue} from './utils';

type TDateSectionProps = {
  value?: Date | null;
  disabled?: boolean;
  hoveredDate?: Date | null;
  minDate?: Date | null;
  maxDate?: Date | null;
  onChange: (value: Date | null) => void;
  onFocus: TEmptyFunction;
  calendarType: TCalendarType;
  placeholder?: string;
};

export const DateSection = React.memo<TDateSectionProps>(
  ({
    value: propValue,
    calendarType,
    hoveredDate,
    disabled,
    onChange,
    onFocus,
    minDate,
    maxDate,
    placeholder,
  }) => {
    const {i18n, t} = useTranslation();
    const inputRef = React.useRef<HTMLInputElement>(null);
    const [value, setValue] = React.useState<string>(formatInputDate(propValue));

    const resizeInput = React.useCallback(() => {
      if (inputRef.current) {
        const hasPlaceholder = !formatInputDate(hoveredDate) && !value;
        const placeholderCompensator =
          hasPlaceholder && i18n.language === 'en' ? WIDE_LETTERS_COMPENSATOR : 0;
        const inputValue = formatInputDate(hoveredDate) || value || '';

        inputRef.current.style.width = `${inputValue.length || inputRef.current.placeholder.length + placeholderCompensator}ch`;
      }
    }, [i18n.language, hoveredDate, value]);

    React.useEffect(() => {
      if (calendarType === ECalendarType.DATE_RANGE) {
        resizeInput();
      }
    }, [i18n.language, calendarType, hoveredDate, value]);

    React.useEffect(() => {
      setValue(formatInputDate(propValue));
    }, [propValue]);

    const handleChange = React.useCallback<React.ChangeEventHandler<HTMLInputElement>>(
      (event) => {
        const valueLength = event.target.value.length;
        const selectionStart = event.target.selectionStart ?? valueLength;

        event.target.value = formatInputValue(event.target.value);

        if (event.target.value.endsWith('.')) {
          event.target.value = event.target.value.slice(0, event.target.value.length - 2);
        }

        const formatDiff = event.target.value.length - valueLength;

        event.target.selectionStart = event.target.selectionEnd = selectionStart + formatDiff;

        const inputValue = formatInputValue(event.target.value);

        setValue(inputValue);

        const date = parseDate(event.target.value, 'dd.MM.yyyy', new Date());

        if (
          event.target.value.length === 10 &&
          isValidDate(date) &&
          isAvailableDate(date, minDate, maxDate)
        ) {
          onChange(date);
        }
      },
      [minDate, maxDate, onChange],
    );

    const handleBlur = React.useCallback<React.ChangeEventHandler<HTMLInputElement>>(() => {
      const date = parseDate(value, 'dd.MM.yyyy', new Date());

      if (value.length !== 10 || !isValidDate(date) || !isAvailableDate(date, minDate, maxDate)) {
        setValue(formatInputDate(propValue));
      }
    }, [value, propValue, maxDate, minDate]);

    return (
      <S.DateSection
        ref={inputRef}
        value={hoveredDate && (!propValue || !isSameDay(hoveredDate, propValue)) ? '' : value}
        disabled={disabled}
        placeholder={
          formatInputDate(hoveredDate) || (placeholder ?? t('EdnaDatePicker:inputPlaceholder'))
        }
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={onFocus}
      />
    );
  },
);
